import { Container, injectable } from "inversify";

import {
  ApiServiceBinder as AppHQServiceBinder,
  IAPIConfiguration as AppHQIAPIConfiguration,
  IHttpClient as AppHQIHttpClient,
  UserProfileServiceInterface
} from "@apphq/apphq-client";
import {
  ApiServiceBinder as RvkServiceBinder,
  IAPIConfiguration as RvkIAPIConfiguration,
  IHttpClient as RvkIHttpClient,
  RvkAdminServiceInterface,
  RvkUserServiceInterface,
  RvkWorkServiceInterface
} from "@apphq/rvk-client";
import {
  AbstractServiceLogger,
  LoggingService,
  LogSink,
  VueLogger,
  ServiceLogger
} from "@apphq/logging";
import { ProfileStore, UserInfoService } from "@apphq/user-profile";
import { store as AuthStore } from "@apphq/vue-keycloak-iframe";

import store from "@/store.js";
import ApiConfiguration from "@/api/ApiConfiguration";
import {
  TokenProvider,
  RoleChecker,
  LoggingServiceAdapter
} from "@/api/services";
import servicePathFactory from "@/api/servicePathFactory";
import { HttpClient } from "@/api/HttpClient";

import { LanguageSelection } from "@/i18n/LanguageSelection";
import i18n from "@/i18n";

import AbilityRulesFactory from "@/AbilityRulesFactory";

const container = new Container();

/** Backend */
container
  .bind<string>("RestServiceBasePath")
  .toDynamicValue(servicePathFactory);

container.bind(AppHQIAPIConfiguration).to(ApiConfiguration);
container.bind(LanguageSelection).toConstantValue({
  get language() {
    return i18n.i18next.language;
  }
});
container
  .bind(AppHQIHttpClient)
  .to(HttpClient)
  .inSingletonScope();
AppHQServiceBinder.with(container);

container.bind(RvkIAPIConfiguration).toService(AppHQIAPIConfiguration);
container.bind(RvkIHttpClient).toService(AppHQIHttpClient);
RvkServiceBinder.with(container);

/** Vuex Modules */
container
  .bind("VuexModuleOptions")
  .toConstantValue((className: string) => ({ store, name: className }));

container
  .bind(AuthStore)
  .toSelf()
  .inSingletonScope();
container
  .bind(ProfileStore)
  .toSelf()
  .inSingletonScope();
container.bind(RoleChecker).toService(AuthStore);
container.bind(TokenProvider).toService(AuthStore);
container.bind(UserInfoService).toService(UserProfileServiceInterface);

const authStore = container.get(AuthStore);
const profileStore = container.get(ProfileStore);

/** Logging */
container.bind(LoggingService).to(LoggingServiceAdapter);
container
  .bind(AbstractServiceLogger)
  .to(ServiceLogger)
  .inSingletonScope();

const vueLogger = container.resolve(VueLogger);

/** Permissions */
const abilityRulesFactory = container.resolve(AbilityRulesFactory);

export const rvkadminClient = container.get(RvkAdminServiceInterface);
export const userClient = container.get(RvkUserServiceInterface);
export const rvkworkClient = container.get(RvkWorkServiceInterface);

export { abilityRulesFactory, authStore, profileStore, vueLogger };
